import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link as LinkIcon } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import {
  getColorWithAlpha,
  getLineHeight,
  useRefWrappedCallback
} from '@elfsight/widget-utils';
import type { MediaValue } from '../../data/sources';
import { usePreviewFetch } from '../../data/utils';
import { useLayout } from '../../layout';
import { Link } from '../../lib/components';
import { getURLHostname, usePostSettings } from '../utils';
import { MediaImage } from './media';

interface PreviewLinkProps {
  media: MediaValue | undefined;
  link?: string;
}

export function PreviewLink({
  media,
  link = media?.url,
  ...forwardingProps
}: PreviewLinkProps) {
  const { rerender } = useLayout();
  const { textColor, borderRadiusPreviewLink: borderRadius = 4 } =
    usePostSettings();
  const onStateChange = useRefWrappedCallback(() => rerender?.());
  const { src, title, description, type } = media ?? {};

  const previewData = usePreviewFetch(link);

  const data = useMemo(() => {
    if (type === 'link') {
      return {
        src,
        title,
        description,
        cover: ''
      };
    }

    if (type === 'audio' && previewData) {
      return {
        ...previewData,
        src: undefined
      };
    }

    return {
      src,
      title,
      description,
      ...previewData
    };
  }, [src, type, title, description, previewData]);

  useEffect(() => {
    if (!onStateChange) {
      return;
    }

    const request = requestAnimationFrame(onStateChange);

    return () => {
      cancelAnimationFrame(request);
    };
  }, [data, onStateChange]);

  const { bgColor, borderColor, domainColor } = useMemo(
    () => ({
      bgColor: getColorWithAlpha(textColor, 0.05),
      borderColor: getColorWithAlpha(textColor, 0.1),
      domainColor: getColorWithAlpha(textColor, 0.5)
    }),
    [textColor]
  );

  if (!link) {
    return null;
  }

  return (
    <Container
      {...forwardingProps}
      href={link}
      hoverEffect="none"
      _textColor={textColor}
      _backgroundColor={bgColor}
      _borderColor={borderColor}
      _borderRadius={borderRadius}
    >
      {(data.cover || data.src) && (
        <MediaImage
          ratio={0.33}
          src={data.cover || data.src}
          alt={data.title || ''}
        />
      )}

      <Content>
        {!!data.title && <Title>{data.title}</Title>}

        {!!data.description && <Description>{data.description}</Description>}

        <Domain _textColor={domainColor}>
          <Icon
            component={LinkIcon}
            size={12}
            fill={domainColor}
            style={{ margin: 0 }}
          />
          <span>{getURLHostname(link)}</span>
        </Domain>
      </Content>
    </Container>
  );
}

const FONT_SIZE = 13;

const Domain = styled.div<{
  _textColor: string;
}>`
  display: flex;
  align-items: center;
  color: ${({ _textColor }) => _textColor};

  & span {
    margin-left: 4px;
  }
`;

const Container = styled(Link)<{
  _textColor: string;
  _borderRadius: number;
  _backgroundColor: string;
  _borderColor: string;
}>`
  display: block;
  width: 100%;
  overflow: hidden;
  transition: background 0.2s ease;
  color: ${({ _textColor }) => _textColor};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  background: ${({ _backgroundColor }) => _backgroundColor};
  border: 1px solid ${({ _borderColor }) => _borderColor};

  &:hover {
    background: ${({ _borderColor }) => _borderColor};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  font-size: ${FONT_SIZE}px;
  line-height: ${getLineHeight({ fontSize: FONT_SIZE })}px;
`;

const Title = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: bold;
`;

const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
